<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import IntegrationConfigFields from '@/js/back-office/components/IntegrationConfigFields.vue';
    import { useIntegrationConfigForm } from '@/js/composables/useIntegrationConfigForm';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetIntegrationConfigOptions,
        useQueryGetIntegrationConfigs,
        useQueryGetIntegrationConfig,
        useMutationSaveIntegrationConfig,
        useMutationDeleteIntegrationConfig,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const { data: systemOptionsData } = useQueryGetIntegrationConfigOptions();

    type Mode = 'list' | 'create' | 'edit';

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const mode = ref<Mode>('list');

    const selectedSystemKey = ref('');
    const systemSearch = ref<string | null>(null);

    const { data: configuredSystemsData } = useQueryGetIntegrationConfigs(selectedTenantId);

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const configuredSystemKeys = computed(
        () => new Set((configuredSystemsData.value ?? []).map((s) => s.systemKey))
    );

    const availableSystemOptions = computed<Option[]>(() =>
        (systemOptionsData.value ?? [])
            .filter((s) => !configuredSystemKeys.value.has(s.systemKey))
            .map((s) => ({ name: s.systemName, value: s.systemKey }))
    );

    const selectedSystem = computed(
        () =>
            (systemOptionsData.value ?? []).find((s) => s.systemKey === selectedSystemKey.value) ??
            null
    );

    const editingSystemKey = computed(() => (mode.value === 'edit' ? selectedSystemKey.value : ''));
    const isConfigExisting = computed(() => mode.value === 'edit');

    const { data: existingConfigData } = useQueryGetIntegrationConfig(
        selectedTenantId,
        editingSystemKey
    );

    const { formValues, canSubmit } = useIntegrationConfigForm(
        selectedTenantId,
        selectedSystem,
        existingConfigData,
        isConfigExisting
    );

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
    };
    const onSystemSelect = (option: Option) => {
        selectedSystemKey.value = option.value;
    };
    const onSystemClear = () => {
        selectedSystemKey.value = '';
    };

    const clearForm = () => {
        selectedSystemKey.value = '';
        systemSearch.value = null;
        formValues.value = {};
    };

    watch(selectedTenantId, () => {
        mode.value = 'list';
        clearForm();
    });

    const startCreate = () => {
        clearForm();
        mode.value = 'create';
    };

    const startEdit = (systemKey: string, systemName: string) => {
        clearForm();
        selectedSystemKey.value = systemKey;
        systemSearch.value = systemName;
        mode.value = 'edit';
    };

    const cancelForm = () => {
        clearForm();
        mode.value = 'list';
    };

    const { mutateAsync: saveMutation, isPending } = useMutationSaveIntegrationConfig();
    const { mutateAsync: deleteMutation } = useMutationDeleteIntegrationConfig();

    const deletingSystemKey = ref<string | null>(null);

    const deleteConfig = async (systemKey: string) => {
        const confirmed = await confirmationDialogStore.openDialog({
            title: __('delete'),
            message: __('back-office-integration-config-delete-confirm'),
        });
        if (!confirmed) return;

        deletingSystemKey.value = systemKey;
        try {
            await deleteMutation({ tenantId: selectedTenantId.value, systemKey });
            cancelForm();
        } finally {
            deletingSystemKey.value = null;
        }
    };

    const submit = async () => {
        if (!canSubmit.value || !selectedSystem.value) return;
        await saveMutation({
            tenantId: selectedTenantId.value,
            systemKey: selectedSystemKey.value,
            fieldValues: formValues.value,
        });
        successDialogStore.openDialog({ message: __('back-office-integration-config-success') });
        cancelForm();
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-integration-config') }}</h1>
        </template>
        <template #content>
            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="integration-config-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onTenantSelect"
                    @clear="onTenantClear"
                />

                <template v-if="selectedTenantId">
                    <!-- List view -->
                    <template v-if="mode === 'list'">
                        <div class="flex flex-col gap-3">
                            <div
                                v-for="config in configuredSystemsData ?? []"
                                :key="config.systemKey"
                                class="flex items-center justify-between rounded-md border border-gray-light px-4 py-3"
                            >
                                <p class="text-body-bold">{{ config.systemName }}</p>
                                <div class="flex gap-2">
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="startEdit(config.systemKey, config.systemName)"
                                    >
                                        {{ __('edit') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        :loading="deletingSystemKey === config.systemKey"
                                        @click="deleteConfig(config.systemKey)"
                                    >
                                        {{ __('delete') }}
                                    </EchoButton>
                                </div>
                            </div>
                            <p
                                v-if="configuredSystemsData?.length === 0"
                                class="text-body text-gray-medium"
                            >
                                {{ __('back-office-integration-config-none') }}
                            </p>
                        </div>
                        <div>
                            <EchoButton type="button" variant="primary" @click="startCreate">
                                {{ __('back-office-integration-config-add') }}
                            </EchoButton>
                        </div>
                    </template>

                    <!-- Form view (create / edit) -->
                    <template v-else>
                        <form class="flex flex-col gap-6" @submit.prevent="submit">
                            <AutoComplete
                                v-model="systemSearch"
                                input-id="integration-config-system"
                                :label="__('back-office-integration-config-select-system')"
                                :options="mode === 'create' ? availableSystemOptions : []"
                                :disabled="mode === 'edit'"
                                @select="onSystemSelect"
                                @clear="onSystemClear"
                            />

                            <IntegrationConfigFields
                                v-if="selectedSystem"
                                v-model="formValues"
                                id-prefix="integration-config-field"
                                :fields="selectedSystem.fields"
                                :stored-secrets="existingConfigData?.storedSecrets ?? []"
                            />

                            <div class="flex justify-between">
                                <EchoButton
                                    v-if="mode === 'edit'"
                                    type="button"
                                    variant="secondary"
                                    :loading="deletingSystemKey === selectedSystemKey"
                                    @click="deleteConfig(selectedSystemKey)"
                                >
                                    {{ __('delete') }}
                                </EchoButton>
                                <div class="flex gap-3">
                                    <EchoButton
                                        type="submit"
                                        variant="primary"
                                        :disabled="!canSubmit"
                                        :loading="isPending"
                                    >
                                        {{ __('back-office-integration-config-save') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="cancelForm"
                                    >
                                        {{ __('cancel') }}
                                    </EchoButton>
                                </div>
                            </div>
                        </form>
                    </template>
                </template>
            </div>
        </template>
    </SettingsPageLayout>
</template>
